import { ReactElement, memo, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ICatalogueSetItem } from "../../../../apis/useGetCatalogueData/types";
import { displayCentsWithDollarSign } from "../../../../common/helpers/stringUtils";
import Condition from "../../../../components/Conditional/Condition";
import I18nText from "../../../../components/I18nText/I18nText";
import AccordionSelector from "../../../../components/elements/Selection/accordion/AccordionSelector";
import { useConfigContext } from "../../../../contexts/ConfigContext/ConfigContext";
import { PDP_INPUT_STATUS } from "../../constants";
import PDPCustomizations from "../customizations/PDPCustomizations";
import { usePDPSetItem } from "./usePDPSetItem";
import { useSetItemBasePrice } from "./useSetItemBasePrice";
import { useSetItemChangeHandler } from "./useSetItemChangeHandler";
import { useSetItemSelection } from "./useSetItemSelection";

interface IProps {
	idx: number;
	setitem: ICatalogueSetItem;
	selectedSetItem?: ICatalogueSetItem | null;
}

const NO_ID: number[] = [];

const PDPSetItem = ({ idx, setitem, selectedSetItem }: IProps): ReactElement => {
	const items = usePDPSetItem(setitem.food_supplier_category_id_list ?? NO_ID);

	const base_price = useSetItemBasePrice(
		items,
		String(setitem.food_supplier_menu_item_id ?? ""),
		setitem.peg_base_price,
		setitem.base_price,
		setitem.customize
	);

	const { selections } = useSetItemSelection(items, base_price);

	const { onChange, customizations, selecteditem } = useSetItemChangeHandler(
		idx,
		setitem,
		items,
		selectedSetItem
	);

	const { t } = useTranslation();
	const { state: bcrs_deposit_rate } = useConfigContext((state) => state.config.bcrs_deposit_rate);

	const memoizedOnChange = useMemo(() => onChange(selections), [onChange, selections]);

	// For non-customizable items, automatically trigger the onChange event when initValues are set
	useEffect(() => {
		if (!setitem.customize && items.length > 0) {
			const defaultId = String(setitem.food_supplier_menu_item_id ?? "");
			const defaultItem = items.find((item) => item.id === defaultId);

			if (defaultItem && selections.length > 0) {
				// Trigger the onChange callback with the default selection
				const changeHandler = onChange(selections);

				changeHandler([defaultId]);
			}
		}
	}, [
		setitem.customize,
		setitem.food_supplier_menu_item_id,
		items.length,
		selections.length,
		onChange,
	]);

	const config = useMemo(() => {
		const error = selecteditem ? "" : t("Error.required_set_item");

		// Use selectedSetItem ID if available, otherwise default behavior
		let initValues: string[] | undefined;

		if (!setitem.customize) {
			// For non-customizable items, always default to the setitem's food_supplier_menu_item_id
			// and ensure it exists in selections before setting
			const defaultId = String(setitem.food_supplier_menu_item_id ?? "");
			const defaultExists = selections.some((selection) => selection.id === defaultId);

			if (defaultExists) {
				initValues = [defaultId];
			}
		} else if (selectedSetItem) {
			// For customizable items, use the selectedSetItem if available
			const selectedId = selectedSetItem.food_supplier_menu_item_id;

			if (selectedId != null) {
				const selectedIdStr = String(selectedId);
				const selectedExists = selections.some((selection) => selection.id === selectedIdStr);

				if (selectedExists) {
					initValues = [selectedIdStr];
				}
			}
		}

		return {
			id: `SetItem-${setitem.external_id}`,
			options: selections,
			label: <Trans i18nKey="Generic.Select" />,
			testId: `SetItemSelect-${setitem.title}`,
			onChange: memoizedOnChange,
			autoCollapse: false,
			initValues,
			allowChange: setitem.customize,
			min: 1,
			erroren: error,
			errorcn: error,
		};
	}, [setitem, selections, memoizedOnChange, selecteditem, selectedSetItem, t]);

	const selected_setitem = useMemo(() => {
		// Use selectedSetItem prop instead of local selecteditem for consistency
		let itemToUse = selectedSetItem || selecteditem;

		// For non-customizable items, always ensure there's a default selection
		if (!setitem.customize) {
			// First priority: use selectedSetItem or selecteditem if available
			if (!itemToUse && config.initValues?.length) {
				const defaultId = config.initValues[0];

				itemToUse = items.find((item) => item.id === defaultId) || null;
			}

			// Fallback: if still no item and we have the default food_supplier_menu_item_id
			if (!itemToUse) {
				const defaultId = String(setitem.food_supplier_menu_item_id ?? "");

				itemToUse = items.find((item) => item.id === defaultId) || null;
			}
		}

		if (!itemToUse) return null;

		return {
			idx,
			item: itemToUse,
		};
	}, [
		selectedSetItem,
		selecteditem,
		idx,
		setitem.customize,
		config.initValues,
		items,
		setitem.food_supplier_menu_item_id,
	]);

	const memoizedSelectedCustomizations = useMemo(
		() => selectedSetItem?.food_supplier_customizations || [],
		[
			selectedSetItem?.food_supplier_menu_item_id,
			selectedSetItem?.food_supplier_customizations?.length,
		]
	);

	// Calculate BCRS deposit for selected item
	const bcrsDepositAmount = useMemo(() => {
		const currentSelectedItem = selected_setitem?.item;

		if (!currentSelectedItem || !currentSelectedItem.bcrs) return 0;

		return currentSelectedItem.bcrs * bcrs_deposit_rate;
	}, [selected_setitem?.item, bcrs_deposit_rate]);

	const bcrsDepositText = useMemo(() => {
		const currentSelectedItem = selected_setitem?.item;

		if (!currentSelectedItem || !currentSelectedItem.bcrs) return "";
		const totalBcrs = currentSelectedItem.bcrs;

		if (totalBcrs > 1) {
			return `${t("cartPage.bcrs.deposit")} × ${totalBcrs}`;
		}

		return t("cartPage.bcrs.deposit");
	}, [selected_setitem?.item, t]);

	return (
		<>
			<div
				className="PDPSetItemContainer"
				data-inputstatus={selected_setitem ? PDP_INPUT_STATUS.OK : PDP_INPUT_STATUS.BAD}
			>
				<h2>
					<I18nText className="OptionTitle" enText={setitem.title} cnText={setitem.title_cn}>
						<span className="Required">*</span>
					</I18nText>
				</h2>
				<AccordionSelector
					key={selectedSetItem?.food_supplier_menu_item_id || `default-${setitem.external_id}`}
					config={config}
				/>
				<Condition check={bcrsDepositAmount > 0}>
					<div className="SetItemBcrsDeposit">
						<span className="BcrsDepositTitle">{bcrsDepositText}</span>
						<span className="BcrsDepositValue">
							+ {displayCentsWithDollarSign(bcrsDepositAmount)}
						</span>
					</div>
				</Condition>
			</div>
			<Condition check={customizations.length > 0}>
				<PDPCustomizations
					customizations={customizations}
					setitem={selected_setitem}
					selectedCustomizations={memoizedSelectedCustomizations}
				/>
			</Condition>
		</>
	);
};

export default memo(PDPSetItem);
